import { DragDropModule } from '@angular/cdk/drag-drop';
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterLink, RouterOutlet } from '@angular/router';
import { SortingComponent } from '@app/taskpane/settings/sort-setting/sort-setting.component';
import { HeaderModule } from '@cdx/ngx-branding';
import { TranslateModule } from '@ngx-translate/core';
import { AddinContextService } from '@service/addin-context.service';
import { TrifoldSortPipe } from '@shared/components/ip-trifold/ip-trifold-sort-pipe';
import { PaginatedAutocompleteComponent } from '@shared/components/paginated-autocomplete/paginated-autocomplete.component';
import { SheetRangeSelectionComponent } from '@shared/components/sheet-range-selection/sheet-range-selection.component';
import { OAuthModule } from 'angular-oauth2-oidc';

import { AddinHeaderComponent } from './shared/components/addin-header/addin-header.component';
import { DocSubTypeMutiselectComponent } from './shared/components/doc-sub-type-multiselect/doc-sub-type-multiselect.component';
import { DocSubTypeSelectComponent } from './shared/components/doc-sub-type-select/doc-sub-type-select.component';
import { InfoDialogComponent } from './shared/components/info-dialog/info-dialog.component';
import { IpDetailComponent } from './shared/components/ip-detail/ip-detail.component';
import { IpIconComponent } from './shared/components/ip-icon/ip-icon.component';
import { IpTrifoldComponent } from './shared/components/ip-trifold/ip-trifold.component';
import { TrifoldFilterDialogComponent } from './shared/components/ip-trifold/trifold-filter-dialog/trifold-filter-dialog.component';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { SearchableComponent } from './shared/components/searchable/searchable.component';
import { WorkflowDetailComponent } from './shared/components/workflow-detail/workflow-detail.component';
import { AuthService } from './shared/service/auth.service';
import { HomeComponent } from './taskpane/home/home.component';
import { ShareLinkDialogComponent } from './taskpane/home/share-link-dialog/share-link-dialog.component';
import { UploadVersionDialogComponent } from './taskpane/home/upload-version-dialog/upload-version-confirm-dialog.component';
import { LoginComponent } from './taskpane/login/login.component';
import { RegExpDialogComponent } from './taskpane/settings/reg-exp-dialog/reg-exp-dialog.component';
import { SettingsComponent } from './taskpane/settings/settings.component';
import { TaskpaneComponent } from './taskpane/taskpane.component';
import { UploadResultsDialogComponent } from './taskpane/uploader/response-dialog/upload-results-dialog.component';
import { UploadTableComponent } from './taskpane/uploader/upload-table/upload-table.container.component';
import { UploaderContainerComponent } from './taskpane/uploader/uploader-container.component';

@NgModule({
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    FormsModule,
    HeaderModule,
    HttpClientModule,
    MatAutocompleteModule,
    MatBadgeModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatOptionModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSidenavModule,
    MatSnackBarModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    OAuthModule.forRoot(),
    ReactiveFormsModule,
    RouterLink,
    RouterOutlet,
    SheetRangeSelectionComponent,
    DragDropModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
    }),
    AddinHeaderComponent,
    DocSubTypeSelectComponent,
    DocSubTypeMutiselectComponent,
    HomeComponent,
    InfoDialogComponent,
    IpDetailComponent,
    IpIconComponent,
    IpTrifoldComponent,
    LoginComponent,
    RegExpDialogComponent,
    SearchableComponent,
    SettingsComponent,
    ShareLinkDialogComponent,
    TaskpaneComponent,
    TrifoldFilterDialogComponent,
    UploaderContainerComponent,
    UploadResultsDialogComponent,
    UploadVersionDialogComponent,
    UploadTableComponent,
    WorkflowDetailComponent,
    LoaderComponent,
    SortingComponent,
    TrifoldSortPipe,
    PaginatedAutocompleteComponent,
  ],
  providers: [
    AuthService,
    {
      provide: APP_INITIALIZER,
      useFactory: (authService: AuthService) => () => authService.initialize(),
      deps: [AuthService],
      multi: true,
    },
    AddinContextService,
    {
      provide: APP_INITIALIZER,
      useFactory: (addinService: AddinContextService) => () => addinService.initialize(),
      deps: [AddinContextService],
      multi: true,
    },
  ],
})
export class AppModule {
  constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
    iconRegistry.addSvgIcon('excel', sanitizer.bypassSecurityTrustResourceUrl('assets/excel-icon.svg'));
  }
}
